import React, { useState } from 'react';
import axios from 'axios';
import { PRODUCT_TYPE } from '../config/product';

// "Add-ons" on a customer's card (migration 104): the company's add-ons this
// customer always has, each with a detail ("6:30 am") when the add-on asks for
// one in Settings. New stops pick them up. They are kept on the root customer,
// so a property card shows and edits its customer's list.
//
// Collapsed until opened, like Service requests beside it; the company's
// add-ons are loaded each time it is opened.

export const DETAIL_MAX = 60;

// "Name" or "Name: detail". A detail is only ever stored where the add-on has
// a detail label, so a stale one never shows.
export const addonBadgeText = (addon) => (
  addon.detail ? `${addon.name}: ${addon.detail}` : addon.name
);

// The badges, for the badge row on the card. An add-on switched off in
// Settings is not picked up by new stops, so it is not shown.
export function CustomerAddonBadges({ addons }) {
  return (addons || [])
    .filter((a) => a.isActive !== false)
    .map((a) => (
      <span key={a.addonId} className="addon-badge">{addonBadgeText(a)}</span>
    ));
}

// The PUT body from the ticks: only active add-ons, and a detail only where
// the add-on asks for one.
export const customerAddonsBody = (companyAddons, ticked) => ({
  addons: companyAddons
    .filter((a) => ticked[a.id] !== undefined)
    .map((a) => {
      const detail = a.detail_label ? (ticked[a.id] || '').trim() : '';
      return { addonId: a.id, detail: detail === '' ? null : detail };
    }),
});

export default function CustomerAddons({ customerId, addons, onSaved }) {
  const [open, setOpen] = useState(false);
  const [companyAddons, setCompanyAddons] = useState(null);
  // addonId -> detail text; a key present means ticked.
  const [ticked, setTicked] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    setError('');
    setMessage('');
    setCompanyAddons(null);
    const next = {};
    (addons || []).forEach((a) => { next[a.addonId] = a.detail || ''; });
    setTicked(next);
    try {
      // This product's add-ons and 'both' only, as Storm Mode and the rates
      // page show; without the param the server returns every product's.
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/service-addons?product=${PRODUCT_TYPE}`);
      setCompanyAddons(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setCompanyAddons([]);
      setError(err.response?.data?.error || 'Could not load the add-ons. Please try again.');
    }
  };

  const toggleOpen = () => {
    if (!open) load();
    setOpen(!open);
  };

  const toggle = (id) => {
    setMessage('');
    setTicked((prev) => {
      const next = { ...prev };
      if (next[id] !== undefined) delete next[id];
      else next[id] = '';
      return next;
    });
  };

  const setDetail = (id, value) => {
    setMessage('');
    setTicked((prev) => ({ ...prev, [id]: value }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/customers/${customerId}/addons`,
        customerAddonsBody(companyAddons, ticked)
      );
      if (onSaved) onSaved(res.data.addons || []);
      setMessage('Saved. Stops added from now on pick these up.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the add-ons. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="customer-addons" style={{ marginTop: '8px' }}>
      <button
        type="button"
        className="btn btn-link btn-sm"
        onClick={toggleOpen}
        aria-expanded={open}
        style={{ padding: 0, fontSize: '0.8125rem' }}
      >
        {open ? '▼' : '▶'} Add-ons
      </button>
      {open && (
        <div className="ca-card">
          <p className="ca-intro">
            Add-ons this customer always has. Stops added from now on pick them up; stops already on a
            route keep what they have.
          </p>
          {error && <p className="ca-error" role="alert">{error}</p>}
          {message && <p className="ca-message">{message}</p>}
          {companyAddons === null && <p className="ca-empty">Loading…</p>}
          {companyAddons && companyAddons.length === 0 && !error && (
            <p className="ca-empty">No add-ons yet. Add them in Settings, Service Add-ons.</p>
          )}
          {companyAddons && companyAddons.length > 0 && (
            <>
              <ul className="ca-list">
                {companyAddons.map((a) => {
                  const on = ticked[a.id] !== undefined;
                  return (
                    <li key={a.id} className="ca-item">
                      <label className="ca-check">
                        <input type="checkbox" checked={on} onChange={() => toggle(a.id)} />
                        {a.name}
                        {a.pricing_type === 'percent' && a.percent !== null && (
                          <span className="ca-hint"> +{Number(parseFloat(a.percent).toFixed(2))}%</span>
                        )}
                      </label>
                      {on && a.detail_label && (
                        <label className="ca-detail">
                          {a.detail_label}
                          <input
                            type="text"
                            className="form-input"
                            value={ticked[a.id]}
                            maxLength={DETAIL_MAX}
                            onChange={(e) => setDetail(a.id, e.target.value)}
                          />
                        </label>
                      )}
                    </li>
                  );
                })}
              </ul>
              <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save add-ons'}
              </button>
            </>
          )}
        </div>
      )}
      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .ca-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin: 12px 0; background: white; }
  .ca-intro { color: #64748b; margin: 0 0 10px; font-size: 0.85rem; }
  .ca-list { list-style: none; margin: 0 0 12px; padding: 0; display: flex; flex-direction: column; gap: 8px; }
  .ca-item { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; }
  .ca-check { display: flex; align-items: center; gap: 8px; min-height: 36px; font-size: 0.9rem; color: #0f172a; }
  .ca-hint { color: #64748b; font-size: 0.8rem; }
  .ca-detail { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: #475569; }
  .ca-detail .form-input { width: 160px; max-width: 100%; }
  .ca-error { color: #b91c1c; font-weight: 600; margin: 0 0 8px; }
  .ca-message { color: #166534; background: #f0fdf4; border-radius: 6px; padding: 8px 10px; margin: 0 0 8px; }
  .ca-empty { color: #64748b; font-size: 0.9rem; margin: 0 0 8px; }
`;
